<template>
  <div>
    <div class="v-stepper elevation-0">
      <div class="v-stepper-items">
        <div
          v-show="currentStep.step === 0"
          class="v-stepper-content text-center step-content"
          data-cy="step0"
          step="0"
        >
          <v-progress-circular class="my-8" color="primary" indeterminate />
        </div>
        <div v-show="currentStep.step === 1" data-cy="step1" step="1">
          <div class="donation-flow">
            <v-slide-x-transition appear>
              <div class="donation-flow__stepper elevation-1">
                <donation-type-view
                  v-if="requestSession"
                  :donation-types="requestSession.donationTypes"
                  :compact="true"
                  @add-step="addStep"
                  @next-step="nextStep"
                  @remove-step="removeStep"
                  @select-donation-type="onDonationTypeSelected"
                />
              </div>
            </v-slide-x-transition>
          </div>
        </div>
        <div v-show="currentStep.step === 2" data-cy="step2" step="2">
          <information-view
            :request-has-external-agent="requestHasExternalAgent"
            :show-previous-button="multipleDonationTypes"
            @add-step="addStep"
            @next-step="nextStep"
            @previous-step="previousStep"
            @remove-step="removeStep"
          />
        </div>
        <div v-show="currentStep.step === 3" data-cy="step3" step="3">
          <case-view
            :show-prev="requestHasOrganizationGroup || !requestHasExternalAgent"
            :request-has-organization-group="requestHasOrganizationGroup"
            @add-step="addStep"
            @next-step="nextStep"
            @previous-step="previousStep"
            @remove-step="removeStep"
          />
        </div>
        <div v-show="currentStep.step === 4" class="step-content" data-cy="step4" step="4">
          <donor-details-view
            :show-previous-button="
              multipleDonationTypes || !requestHasExternalAgent || !requestHasOnlyOneCase
            "
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </div>
        <div v-show="currentStep.step === 5" class="step-content" data-cy="step5" step="5">
          <donation-details-view @next-step="nextStep" @previous-step="previousStep" />
        </div>
        <div v-show="currentStep.step === 6" data-cy="step6" step="6">
          <payment-delivery-view @next-step="nextStep" @previous-step="previousStep" />
        </div>
        <div v-show="currentStep.step === 7" class="step-content" data-cy="step7" step="7">
          <summary-view-v1 @next-step="nextStep" @previous-step="previousStep" @step="setStep" />
        </div>
        <div v-show="currentStep.step === 8" class="step-content" data-cy="step8" step="8">
          <payment-view @reset="reset" @step="setStep" />
        </div>
        <div v-show="currentStep.step === 9" class="step-content" data-cy="step9" step="9">
          <div class="donation-flow">
            <div class="donation-flow__stepper elevation-1">
              <success-view compact disable-elevation @reset="reset" />
            </div>
          </div>
        </div>
        <div v-show="currentStep.step === 10" class="step-content" data-cy="step10" step="10">
          <paylink-payment-view @step="setStep" />
        </div>
      </div>
    </div>
    <logo-footer />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import DonationTypeView from '@/views/DonationTypeView.vue';
import InformationView from '@/views/InformationView.vue';
import CaseView from '@/views/CaseView.vue';
import { RequestSession } from '@/interfaces/requestSession';
import { ENDPOINTS, get } from '@/api/mainApi';
import LogoFooter from '@/components/global/LogoFooter.vue';
import DonorDetailsView from '@/views/DonorDetailsView.vue';
import DonationDetailsView from '@/views/DonationDetailsView.vue';
import PaymentDeliveryView from '@/views/PaymentDeliveryView.vue';
import SummaryViewV1 from '@/views/SummaryViewV1.vue';
import PaymentView from '@/views/PaymentView.vue';
import PaylinkPaymentView from '@/views/PaylinkPaymentView.vue';
import SuccessView from '@/views/SuccessView.vue';
import { DonationStep } from '@/interfaces/donationStep';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { DonationType, Type } from '@/classes/DonationType';
import { getDonationStep, getDonationSteps } from '@/utils/donationStepUtils';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';

import { useDonationReset } from '@/composables/useDonationReset';

import store from '@/store';

const props = defineProps<{
  multipleDonationTypes?: boolean;
  requestHasExternalAgent?: boolean;
  requestHasOnlyOneCase?: boolean;
  loading?: boolean;
  definedStep?: number | null;
}>();

// Data from component and mixin
const availableSteps = ref([] as DonationStep[]);
const sessionError = ref(false);
const defaultDonationGuideVersion = ref(DonationGuideVersion.V1);

// Methods from mixin (defined early)
const getDonationStepFlowVersion = (): DonationGuideVersion => {
  return store.state.donationGuideVersion || defaultDonationGuideVersion.value;
};

const getDonationStepLocal = (stepNumber: number): DonationStep | undefined => {
  return getDonationStep(stepNumber, getDonationStepFlowVersion());
};

const getInitialSteps = (): DonationStep[] => {
  return [...getDonationSteps(getDonationStepFlowVersion())];
};

const setDonationGuideVersion = (donationGuideVersion: DonationGuideVersion) => {
  store.commit('setDonationGuideVersion', donationGuideVersion);
};

const resetSteps = () => {
  availableSteps.value = getDonationSteps(getDonationStepFlowVersion());
};

const previousStep = () => {
  const currentStepNumber: number = (currentStep.value as DonationStep).step;
  const previousSteps = availableSteps.value
    .map((step: DonationStep) => step.step)
    .filter((stepNumber: number) => stepNumber < currentStepNumber)
    .sort((a: number, b: number) => b - a);

  if (previousSteps.length > 0) {
    store.commit('updateCurrentStep', previousSteps[0]);
    nextTick(() => {
      scrollToTopInstant();
    });
  }
};

const nextStep = () => {
  const currentStepNumber: number = (currentStep.value as DonationStep).step;
  const nextSteps = availableSteps.value
    .map((step: DonationStep) => step.step)
    .filter((stepNumber: number) => stepNumber > currentStepNumber)
    .sort((a: number, b: number) => a - b);

  if (nextSteps.length > 0) {
    try {
      scrollToTopInstant();
      store.commit('updateCurrentStep', nextSteps[0]);
    } catch {
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: 'Error setting next step',
      });
    }
  } else {
    store.commit('setSnackbar', { active: true, color: 'warning', text: 'No next step available' });
  }
};

const removeStep = (stepToRemove: number) => {
  const index = availableSteps.value.findIndex((val: DonationStep) => val.step === stepToRemove);
  if (index >= 0) {
    availableSteps.value.splice(index, 1);
  }
};

const addStep = (stepToAdd: number) => {
  let guideStep = availableSteps.value.find((val: DonationStep) => val.step === stepToAdd);
  if (!guideStep) {
    guideStep = getDonationStepLocal(stepToAdd);
    if (guideStep) {
      availableSteps.value.push(guideStep);
      availableSteps.value.sort((a, b) => a.step - b.step);
    }
  }
};

const setStep = (step: number) => {
  try {
    store.commit('updateCurrentStep', step);
    nextTick(() => {});
  } catch {
    store.commit('setSnackbar', { active: true, color: 'error', text: 'Error setting step' });
  }
};

const scrollToTopInstant = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
};

const { resetDonation } = useDonationReset();

const reset = async () => {
  await resetDonation(donation.value);

  setStep(availableSteps.value[1].step);
};

// Computed
const requestSession = computed(() => store.state.requestSession);
const donation = computed(() => store.state.donation);
const currentStep = computed<DonationStep>({
  get: () =>
    getDonationStepLocal(store.state.currentStep) ||
    getDonationStepLocal(getInitialSteps()[0].step) ||
    ({ step: store.state.currentStep } as DonationStep),
  set: (val) => {
    // Accept either a step object or a plain number
    const stepNumber = typeof val === 'number' ? val : (val?.step ?? store.state.currentStep);
    store.commit('updateCurrentStep', stepNumber);
  },
});

// Steps enum
const steps = computed((): typeof stepV1 => {
  return stepV1;
});

const adjustAvailableSteps = () => {
  if (!donation.value || !donation.value.donationType) return;

  const isMemoryGift = donation.value.donationType.type === Type.MEMORY_GIFT;
  const isContribution = donation.value.donationType.type === Type.CONTRIBUTION;
  const hasExternalAgent = props.requestHasExternalAgent;

  // Remove INFORMATION step if not memory gift or has external agent
  if (!isMemoryGift || hasExternalAgent) {
    removeStep(steps.value.INFORMATION);
  } else {
    addStep(steps.value.INFORMATION);
  }

  // Remove CASE step if not memory gift
  if (!isMemoryGift) {
    removeStep(steps.value.CASE);
  } else {
    addStep(steps.value.CASE);
  }

  // For contributions on V1, remove the donation details step so Donor (4) -> Payment (6)
  if (isContribution) {
    const orgGroup = requestSession.value?.organizationGroup;
    const activeCauses = orgGroup?.donationCauses?.filter((c: any) => c.active) ?? [];
    if (activeCauses.length <= 1) {
      removeStep(steps.value.DONATION_DETAILS);
    } else {
      addStep(steps.value.DONATION_DETAILS);
    }
  } else {
    addStep(steps.value.DONATION_DETAILS);
  }
};

const onDonationTypeSelected = (donationType: DonationType) => {
  // For org group contribution flows, auto-select the donation cause here so it is always
  // present in the payload even when the DONATION_DETAILS step is skipped.
  let donationCause: any = null;
  if (donationType.type === Type.CONTRIBUTION) {
    const orgGroup = requestSession.value?.organizationGroup;
    if (orgGroup) {
      const activeCauses = (orgGroup.donationCauses ?? []).filter((c: any) => c.active);
      if (activeCauses.length === 1) {
        // Exclude the organizationGroup back-reference to avoid sending a deeply nested /
        // circular payload to the server. Vue 2 never included this field on the cause.
        const { organizationGroup: _o1, pingPaymentsMerchant: _o2, pennybridgeCause: _o3, pennybridgeId: _o4, entityType: _o5, createdAt: _o6, updatedAt: _o7, ...causeWithoutOrg } = activeCauses[0] as any;
        donationCause = causeWithoutOrg;
      }
    }
  }

  const donationPayload = {
    donationType,
    donor: {
      address: { address1: '', address2: '', address3: '', city: '', country: '', postcode: '' },
      email: '',
      firstname: '',
      surname: '',
      socialSecurityNumber: '',
      token: '',
    },
    attributeValues: [],
    paymentSpecification: null,
    deliveryMethod: null,
    donationCause,
    donationImageId: null,
    sharePersonalInfoWithOrganization: true,
  } as any;
  store.commit('updateDonation', donationPayload);
  const version =
    donationType.type === Type.CONTRIBUTION ? DonationGuideVersion.V1 : DonationGuideVersion.V2;
  setDonationGuideVersion(version);

  // Reset and adjust available steps immediately so nextStep behaves deterministically
  resetSteps();
  adjustAvailableSteps();

  nextTick(() => {
    const stepsVal = version === DonationGuideVersion.V2 ? stepV2 : stepV1;
    const newStep: number =
      donationType.type === Type.CONTRIBUTION ? stepV1.DONOR : stepsVal.INFORMATION;
    setStep(newStep);
  });
};

// Watch
watch(currentStep, () => {
  verifyRequestSessionToken();
});
watch(
  () => props.definedStep,
  (value: number) => {
    if (value > 0) {
      setStep(value);
    }
  }
);
watch(
  () => store.state.donationGuideVersion,
  () => {
    resetSteps();
    adjustAvailableSteps();
  }
);
watch(
  () => donation.value?.donationType,
  () => {
    adjustAvailableSteps();
  },
  { deep: true }
);

// Created
availableSteps.value = getInitialSteps();
adjustAvailableSteps();

// Mounted
onMounted(() => {
  if (props.definedStep) {
    setStep(props.definedStep);
  }
});

// Methods from component
const getPaymentCodeParam = (): string | null => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams.has('paymentCode')) {
    return urlParams.get('paymentCode');
  }
  return null;
};
const getRequestSession = async (handleError: boolean) => {
  return await get(ENDPOINTS.SESSIONS_COOKIE_REQUEST, {}, handleError);
};
const verifyRequestSessionToken = async () => {
  if (!getPaymentCodeParam() && requestSession.value && requestSession.value.token) {
    const reqSession: RequestSession = await getRequestSession(false);
    if (reqSession && reqSession.token && reqSession.token != requestSession.value.token) {
      sessionError.value = true;
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/donation-flow/styles.scss';
</style>
